/**
 * Derives the user-facing "position" view of an alert slot row for the
 * dashboard (In a long / In a short / Flat / Idle) plus the per-indicator dot
 * state. This keeps the trading-friendly language in one place so the summary
 * strip and the Live trades panel stay in sync.
 */
import { AlertSlotMonitorRow } from '../types/dashboard';

export type TradeStateKey = 'in-long' | 'in-short' | 'flat' | 'idle';

export interface TradeStateInfo {
  key: TradeStateKey;
  /** Chip label, e.g. "In a long", "Flat — 2 of 3 aligned", "Idle — no signals yet". */
  label: string;
}

function activeSlotCount(row: AlertSlotMonitorRow): number {
  return row.slots.filter((s) => s === 'up' || s === 'down' || s === 'active').length;
}

function hasAnySignal(row: AlertSlotMonitorRow): boolean {
  return Boolean(row.lastAttemptAt || row.lastTriggeredAt || row.lastSuccessAt);
}

function isShortPosition(row: AlertSlotMonitorRow): boolean {
  const p = (row.positionType || '').toLowerCase();
  return p.includes('short') || p.includes('down') || p.includes('sell') || p.includes('bear');
}

/** Map a slot row to a plain-language position state. */
export function deriveTradeState(row: AlertSlotMonitorRow): TradeStateInfo {
  const isBmta = row.alertKind === 'bidirectional_multi';
  const total = row.slots.length;
  const active = activeSlotCount(row);

  if (isBmta) {
    if (row.consensus === 'all_up') return { key: 'in-long', label: 'In a long' };
    if (row.consensus === 'all_down') return { key: 'in-short', label: 'In a short' };
  } else if (row.consensus === 'all_active') {
    return isShortPosition(row)
      ? { key: 'in-short', label: 'In a short' }
      : { key: 'in-long', label: 'In a long' };
  }

  // Not fully aligned.
  if (!hasAnySignal(row) && active === 0) {
    return { key: 'idle', label: 'Idle — no signals yet' };
  }
  if (row.lastSuccessAt) {
    return { key: 'flat', label: 'Flat — alignment broke' };
  }
  if (total > 0) {
    return { key: 'flat', label: `Flat — ${active} of ${total} aligned` };
  }
  return { key: 'flat', label: 'Flat' };
}

export interface TradeStateSummary {
  'in-long': number;
  'in-short': number;
  flat: number;
  idle: number;
}

/** Count how many alert rows fall into each position bucket. */
export function summarizeTradeStates(rows: AlertSlotMonitorRow[]): TradeStateSummary {
  const summary: TradeStateSummary = { 'in-long': 0, 'in-short': 0, flat: 0, idle: 0 };
  for (const row of rows) {
    summary[deriveTradeState(row).key] += 1;
  }
  return summary;
}

export type SlotTone = 'up' | 'down' | 'neutral';

export interface SlotVisual {
  tone: SlotTone;
  /** Short state word for the indicator dot, e.g. "bullish", "waiting". */
  stateText: string;
  /** User-defined indicator name, else the 1-based slot index. */
  name: string;
}

/** Resolve the colored dot + label shown for a single indicator slot. */
export function slotVisual(row: AlertSlotMonitorRow, index: number): SlotVisual {
  const state = row.slots[index];
  const isBmta = row.alertKind === 'bidirectional_multi';
  const name = row.slotDescriptions?.[index]?.trim() || String(index + 1);
  if (isBmta) {
    if (state === 'up') return { tone: 'up', stateText: 'bullish', name };
    if (state === 'down') return { tone: 'down', stateText: 'bearish', name };
    return { tone: 'neutral', stateText: 'waiting', name };
  }
  if (state === 'active') return { tone: 'up', stateText: 'triggered', name };
  return { tone: 'neutral', stateText: 'waiting', name };
}
